<template>
    <ModalDialogContainer ref="modalContainer" v-model:display="displayStatus">
        <div class="modal-dialog modal-lg" role="document" @click="stopPropagationEvent">
            <div class="modal-header">
                <div class="modal-title">
                    <router-link class="modal-link" to="#" @click="close" :title="$t('Go back')">
                        <i class="fa-solid fa-arrow-left-long"></i>
                        {{ $t("Go back") }}
                    </router-link>
                    {{ $t("Certification details") }}
                </div>
                <button type="button" class="modal-close-btn" :title="$t('Close')" @click="close">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="modal-body">
                <div class="table-responsive no-margin no-border" v-if="attestation">
                    <table class="table">
                        <tr>
                            <td class="bold on-line">{{ $t("Certification type") }}</td>
                            <td>
                                <BadgeComponent
                                    class="certification-type"
                                    :text="renderAttestationType(attestation.type)"
                                    :style-type="BadgeStyle.BORDER"
                                    :shape="BadgeShape.BADGE"
                                    :color="ColorAccent.NEUTRAL"
                                />
                            </td>
                        </tr>

                        <tr>
                            <td class="bold on-line">{{ $t("Certificator") }} ({{ $t("ID") }})</td>
                            <td>{{ attestation.attestator || "-" }}</td>
                        </tr>

                        <tr v-if="attestation.attestatorName">
                            <td class="bold on-line">{{ $t("Attestator") }} ({{ $t("Name") }})</td>
                            <td>{{ attestation.attestatorName || "-" }}</td>
                        </tr>

                        <tr>
                            <td class="bold on-line">{{ $t("Subject") }} ({{ $t("ID") }})</td>
                            <td>{{ subject || "-" }}</td>
                        </tr>

                        <tr v-if="subjectName">
                            <td class="bold on-line">{{ $t("Subject") }} ({{ $t("Name") }})</td>
                            <td>{{ subjectName || "-" }}</td>
                        </tr>

                        <tr>
                            <td class="bold on-line">{{ $t("Expiration date") }}</td>
                            <td>{{ renderDateAndTime(attestation.expiration, $t) }}</td>
                        </tr>
                    </table>
                </div>

                <div class="container">
                    <h3>{{ $t("Attributes") }}</h3>
                    <div class="table-responsive">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>{{ $t("Attribute") }}</th>
                                    <th>{{ $t("Value") }}</th>
                                </tr>
                            </thead>
                            <tr v-for="(a, i) in attestation.attributes" :key="i">
                                <td class="bold">{{ $t("Attribute") }}: {{ a.name }}</td>
                                <td>{{ a.value }}</td>
                            </tr>
                        </table>
                    </div>
                </div>

                <div class="container no-border">
                    <table class="table">
                        <tr class="no-border">
                            <td colspan="2">
                                <CodeBlock :language="'raw'" :code="attestation.raw" :name="$t('Raw attestation')"></CodeBlock>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
        </div>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useVModel } from "../../utils/v-model";
import { DataSpaceKnownParticipantAttestation } from "@/api/definitions";
import CodeBlock from "../utils/CodeBlock.vue";
import { renderDateAndTime } from "@/utils/time-utils";
import BadgeComponent from "../ui/BadgeComponent.vue";
import { BadgeShape, BadgeStyle } from "@/utils/enums/badges";
import { ColorAccent } from "@/utils/enums/colors";

export default defineComponent({
    components: {
        CodeBlock,
        BadgeComponent,
    },
    name: "AttestationViewModal",
    emits: ["update:display"],
    props: {
        display: Boolean,
        attestation: Object as PropType<DataSpaceKnownParticipantAttestation>,
        subject: String,
        subjectName: String,
    },
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
        };
    },
    data() {
        return {
            BadgeStyle: BadgeStyle,
            BadgeShape: BadgeShape,
            ColorAccent: ColorAccent,
        };
    },
    methods: {
        close: function () {
            this.$closeModal();
        },

        escapeToClose: function (event) {
            if (event.key === "Escape") {
                this.close();
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        renderAttestationType: function (type: string) {
            switch (type) {
                case "x509":
                    return this.$t("X.509 Signed Certificate");
                case "did-jwt":
                    return this.$t("JWT DID Attestation");
                default:
                    return "-";
            }
        },

        renderDateAndTime: renderDateAndTime,
    },
    mounted: function () {
        if (this.display) {
            this.$autoFocus();
        }
    },
    beforeUnmount: function () {},
    watch: {
        display: function () {
            if (this.display) {
                this.$autoFocus();
            }
        },
    },
});
</script>

<style scoped>
.modal-body {
    padding: 0 var(--space-6) var(--space-6);
}

.container {
    display: inline-flex;
    flex-direction: column;
}

.certification-type {
    width: fit-content !important;
}

h3 {
    font-size: var(--text-sm);
    margin-bottom: 0;
    margin-left: 9px;
}

thead,
thead th,
thead tr {
    background-color: white !important;
    border: none !important;
    color: var(--color-text-neutral-subtle) !important;
}

.table-responsive {
    border: none;
}

tr {
    border-top: var(--border-width-base) solid var(--color-border-neutral-subtle);

    &.no-border {
        border: none !important;
    }
}

td {
    border-bottom: none !important;
}
</style>
